import { LinkOutlined } from '@ant-design/icons'
import { Sender } from '@ant-design/x'
import {
  AppSession,
  ChatMessage,
  Organization,
  OrganizationMember,
} from '@prisma/client'
import { Badge, Button, Flex, GetRef, Typography } from 'antd'
import React, { useEffect, useRef } from 'react'
import { DesignSystem } from '~/core/designSystem'
import { InputUpload } from '~/features/formConfiguration/client/components/CustomInputs/InputUpload'
import { TypingType } from '../../hooks/useParticipantsTyping'
import { useSendMessage } from '../../hooks/useSendMessage'
import { LoaderDots } from '../LoaderDots'

type Props = {
  suggestions?: string[]
  conversationId: string
  appSession: AppSession
  organization: Organization
  organizationMember: OrganizationMember
  messageParent?: ChatMessage
  isUploadAttachments?: boolean
  typeAttachments?: string[]
  typing: TypingType
  onSentOptimist: (message: ChatMessage) => void
  onSent: (message: ChatMessage) => void
}

export const SendMessageInput: React.FC<Props> = ({
  suggestions = [],
  conversationId,
  messageParent,
  appSession,
  organization,
  organizationMember,
  isUploadAttachments = false,
  typeAttachments = [],
  typing,
  onSentOptimist,
  onSent,
}) => {
  const senderRef = useRef<GetRef<typeof Sender>>(null)

  useEffect(() => {
    const timeout = setTimeout(() => {
      focusOnSender()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const isTyping = !!typing[conversationId]

  const isUsageLimitReached =
    organization?.countTokens > organization?.maxTokens

  const isMobile = DesignSystem.use().isMobile
  const isTouchDevice = DesignSystem.use().isTouchDevice

  const typesAllowed = typeAttachments?.reduce((acc, key) => {
    acc[key] = true
    return acc
  }, {})

  const focusOnSender = () => {
    if (senderRef.current) {
      senderRef.current.focus?.({ preventScroll: false, cursor: 'end' })
    }
  }

  const handleSuggestionSelected = (suggestion: string) => {
    setContent(suggestion)
    focusOnSender()
  }

  const {
    isLoading,
    content,
    setContent,
    send,
    attachments,
    setAttachments,
    isHeaderOpen,
    setHeaderOpen,
  } = useSendMessage({
    conversationId,
    messageParent,
    organization,
    organizationMember,
    onSent,
    onSentOptimist,
  })

  const isDisabled =
    appSession?.status !== 'ACTIVE' ||
    appSession?.statusOnboarding === 'PENDING' ||
    appSession?.statusOnboarding === 'SUBMITTED' ||
    isUsageLimitReached ||
    isLoading

  const hasContent = content?.length > 0
  const hasSuggestions = suggestions?.length > 0

  const SenderHeader = (
    <Sender.Header
      title="Attachments"
      open={isHeaderOpen}
      onOpenChange={setHeaderOpen}
      styles={{
        content: {
          padding: 0,
        },
      }}
    >
      <InputUpload
        maxFileSizeMB={25}
        typesAllowed={typesAllowed}
        isRequestSkipped
        disabled={isDisabled}
        onChange={value => setAttachments(_ => value)}
        formValuesConfiguration={{ organizationId: organization.id }}
        getDropContainer={() => senderRef.current?.nativeElement}
      />
    </Sender.Header>
  )

  return (
    <>
      <Flex className="px-4 w-full" vertical>
        <Sender
          loading={isLoading || isTyping}
          disabled={isDisabled}
          actions={(_, info) => {
            const { SendButton } = info.components

            if (isTyping) {
              return <SendButton disabled />
            }

            if (isLoading) {
              return (
                <Button
                  shape="circle"
                  type="text"
                  disabled
                  icon={<LoaderDots />}
                />
              )
            }

            return <SendButton />
          }}
          value={content}
          placeholder={'Write message'}
          onChange={setContent}
          onSubmit={send}
          submitType={isMobile && isTouchDevice ? 'shiftEnter' : 'enter'}
          style={{
            borderRadius: '25px',
            boxShadow: 'none',
          }}
          styles={{
            input: {
              paddingLeft: !isUploadAttachments ? 8 : undefined,
            },
          }}
          ref={senderRef}
          header={SenderHeader}
          prefix={
            isUploadAttachments ? (
              <Badge dot={attachments.length > 0 && !isHeaderOpen}>
                <Button
                  onClick={() => setHeaderOpen(!isHeaderOpen)}
                  type="text"
                  icon={<LinkOutlined />}
                />
              </Badge>
            ) : null
          }
        />
        {hasSuggestions && (
          <Flex gap={8} className="mt-2">
            {!hasContent &&
              suggestions.map(suggestion => (
                <Button
                  key={suggestion}
                  type="dashed"
                  shape="round"
                  // icon={<EditOutlined />}
                  onClick={() => handleSuggestionSelected(suggestion)}
                >
                  <Typography.Text type="secondary">
                    {suggestion}
                  </Typography.Text>
                </Button>
              ))}
          </Flex>
        )}
      </Flex>
    </>
  )
}
